/** Static option lists for the action inspectors — the genuinely-fixed vocabularies (operations,
 *  channels, writable field sets). The dynamic objects (tags, pipelines, users) are now fetched from
 *  their real stores in the panels; they no longer live here. */
import type { ListCopy } from 'lib/listCopy'
import { LOST_REASONS } from 'app/types/entities'
import { FIELD_VALUE_OPTIONS } from 'app/lib/workflows/fieldValueOptions'

export type Option = { id: string; name: string }

// Task.dueDate is required, so there's no "no due date" option — the task is always due either a
// number of days after it runs, or on a fixed calendar date.
export const DUE_TYPES = [
  { value: `relative`, label: `A number of days after it runs` },
  { value: `absolute`, label: `On a specific date` },
] as const

// What happens when a contact re-triggers a workflow they're already in (maps to the engine's
// WorkflowReenrollmentPolicy).
// Framed as the HubSpot-style question "if a contact triggers this again, run it again?" so the choice
// reads plainly instead of as engine jargon.
export const REENROLLMENT_POLICIES = [
  // `ignore` is one-at-a-time, NOT once-ever: the engine skips enrollment only while an ACTIVE run
  // exists (getActiveByContactAndWorkflow). A finished run doesn't block the next trigger, which is
  // exactly what recall's flywheel relies on. The old label ("No, only once per contact") promised
  // once-ever, understating what would send — the dangerous direction for a messaging product.
  { value: `ignore`, label: `Not while they're already in it` },
  { value: `restart`, label: `Yes, restart from the top` },
  { value: `allow_multiple`, label: `Yes, run another alongside` },
] as const

/** One-line consequence preview shown under the re-enrollment control, so the effect is explicit. */
export const REENROLLMENT_CONSEQUENCE: Record<string, string> = {
  ignore: `New triggers are ignored while a run is in progress. Once it finishes, a later trigger can enroll them again.`,
  restart: `Their current run is cancelled and they start over from the top.`,
  allow_multiple: `A second run starts in parallel with the one they're already in.`,
}

export const TAG_OPERATIONS = [
  { value: `add`, label: `Add tags` },
  { value: `remove`, label: `Remove tags` },
] as const

// Real writable Contact fields (subset of `UpdateContactInput`). Owner is read-only (`leadOwner`),
// and there's no lead-score/source scalar — those were fabricated. Pipeline stage lives on the
// pipeline (the Add-to-pipeline node), not here.
export const CONTACT_EDITABLE_FIELDS = [
  { value: `leadStatus`, label: `Lead status` },
  { value: `lifecycleStage`, label: `Lifecycle stage` },
  { value: `channel`, label: `Channel` },
  { value: `isQualified`, label: `Qualified` },
  { value: `preferredCommunicationMethod`, label: `Preferred contact method` },
  { value: `preferredName`, label: `Preferred name` },
  { value: `closedLostReason`, label: `Closed-lost reason` },
] as const

const asChoices = (values: readonly string[]) => values.map((value) => ({ value, label: value }))

// What the contact drawer offers. Closed Won is system-set; `Closed Lost: Other` is the legacy `Other`.
export const EDITABLE_FIELD_CHOICES: Readonly<Record<string, ReadonlyArray<{ value: string; label: string }>>> = {
  leadStatus: asChoices([`Needs Action`, `In Progress`, `Scheduled`, `Closed Lost`]),
  lifecycleStage: FIELD_VALUE_OPTIONS.lifecycleStage,
  isQualified: [
    { value: `true`, label: `Qualified` },
    { value: `false`, label: `Not qualified` },
  ],
  closedLostReason: asChoices(LOST_REASONS.filter((reason) => reason !== `Closed Lost: Other`)),
}

// Writable Activity fields (`UpdateActivityInput`). Tags are handled by the Tag action, not here.
export const ACTIVITY_EDITABLE_FIELDS = [
  { value: `note`, label: `Note` },
  { value: `needsReview`, label: `Needs review` },
] as const

/** Editable fields for a given target object — contact or the triggering activity. */
export function editableFieldsFor(target: `contact` | `activity`): ReadonlyArray<{ value: string; label: string }> {
  return target === `activity` ? ACTIVITY_EDITABLE_FIELDS : CONTACT_EDITABLE_FIELDS
}

export function editableFieldLabel(value?: string): string | undefined {
  return [...CONTACT_EDITABLE_FIELDS, ...ACTIVITY_EDITABLE_FIELDS].find((field) => field.value === value)?.label
}

// `append` was removed: every writable Contact field is a scalar, so set/clear are the only real ops.
export const EDIT_OPERATIONS = [
  { value: `set`, label: `Set to` },
  { value: `clear`, label: `Clear` },
] as const

export const NOTIFY_CHANNELS = [
  { value: `email`, label: `Email` },
  { value: `in_app`, label: `In-app` },
  { value: `both`, label: `Email + in-app` },
] as const

// Add vs move are the same upsert under the hood — this is the user's intent, so the card reads
// right (e.g. "add at top, then move stage downstream" doesn't look like re-adding).
export const pipelineOperations = (copy: ListCopy) =>
  [
    { value: `add`, label: copy.addToList },
    { value: `move`, label: `Move to a stage` },
  ] as const
